import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { UserContext } from '../App';
import { useTranslation } from '../i18n';
import { authAPI } from '../api';
import Header from '../components/Header';
import { User, Globe, Bell, Shield, LogOut, Check, Lock, UserX, CreditCard, Mail } from 'lucide-react';
import { toast } from 'sonner';

const Settings = () => {
  const { user, setUser } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, setLanguage } = useTranslation();

  // /settings#privacy (from the billing cancel dialog) lands on the Privacy & Data card;
  // client-side navigation does not scroll to a hash by itself.
  useEffect(() => {
    if (location.hash === '#privacy') {
      const el = document.getElementById('privacy');
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'start' });
    }
  }, [location.hash]);
  
  const [notifications, setNotifications] = useState({
    email_deals: true,
    email_weekly: true,
    email_marketing: false,
  });
  const [saving, setSaving] = useState(false);

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      setUser(null);
      navigate('/');
      toast.success(lang === 'en' ? 'Logged out successfully' : 'Sesión cerrada');
    } catch (error) {
      console.error('Logout failed:', error);
      setUser(null);
      navigate('/');
    }
  };

  const saveNotificationSettings = async () => {
    setSaving(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      toast.success(lang === 'en' ? 'Settings saved!' : '¡Configuración guardada!');
    } catch (error) {
      toast.error(lang === 'en' ? 'Failed to save settings' : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 max-w-3xl">
        {/* Page Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary mb-1 sm:mb-2">
            {lang === 'en' ? 'Settings' : 'Configuración'}
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground">
            {lang === 'en' ? 'Manage your account preferences' : 'Gestiona las preferencias de tu cuenta'}
          </p>
        </div>

        {/* Profile Section */}
        <div className="bg-white rounded-xl shadow-sm border border-border p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-secondary/10 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 sm:w-6 sm:h-6 text-secondary" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-semibold">
                {lang === 'en' ? 'Profile' : 'Perfil'}
              </h2>
              <p className="text-muted-foreground text-xs sm:text-sm">
                {lang === 'en' ? 'Your account information' : 'Información de tu cuenta'}
              </p>
            </div>
          </div>

          {user ? (
            <div className="space-y-3 sm:space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-2 sm:py-3 border-b border-border gap-1">
                <span className="text-muted-foreground text-sm">{lang === 'en' ? 'Email' : 'Correo'}</span>
                <span className="font-medium text-sm sm:text-base truncate">{user.email}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-2 sm:py-3 border-b border-border gap-1">
                <span className="text-muted-foreground text-sm">{lang === 'en' ? 'Name' : 'Nombre'}</span>
                <span className="font-medium text-sm sm:text-base">{user.full_name || user.name || 'Not set'}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-2 sm:py-3 border-b border-border gap-1">
                <span className="text-muted-foreground text-sm">{lang === 'en' ? 'Plan' : 'Plan'}</span>
                <span className="px-3 py-1 bg-secondary/10 text-secondary rounded-full text-xs sm:text-sm font-medium w-fit">
                  {user.plan_id?.toUpperCase() || 'FREE'}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-2 sm:py-3 gap-1">
                <span className="text-muted-foreground text-sm">{lang === 'en' ? 'Member since' : 'Miembro desde'}</span>
                <span className="font-medium text-sm sm:text-base">
                  {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                </span>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-muted-foreground mb-4 text-sm">
                {lang === 'en' ? 'Sign in to view your profile' : 'Inicia sesión para ver tu perfil'}
              </p>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-2 bg-secondary text-white rounded-lg font-medium hover:bg-blue-700 transition-all text-sm"
              >
                {lang === 'en' ? 'Sign In' : 'Iniciar Sesión'}
              </button>
            </div>
          )}
        </div>

        {/* Language Settings */}
        <div className="bg-white rounded-xl shadow-sm border border-border p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center">
              <Globe className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-semibold">
                {lang === 'en' ? 'Language' : 'Idioma'}
              </h2>
              <p className="text-muted-foreground text-xs sm:text-sm">
                {lang === 'en' ? 'Choose your preferred language' : 'Elige tu idioma preferido'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <button
              onClick={() => setLanguage('en')}
              className={`p-3 sm:p-4 rounded-lg border-2 transition-all flex items-center justify-between ${
                lang === 'en' 
                  ? 'border-secondary bg-secondary/5' 
                  : 'border-border hover:border-slate-300'
              }`}
              data-testid="lang-en"
            >
              <div className="flex items-center gap-2 sm:gap-3">
                <span className="text-xl sm:text-2xl">🇺🇸</span>
                <span className="font-medium text-sm sm:text-base">English</span>
              </div>
              {lang === 'en' && <Check className="w-4 h-4 sm:w-5 sm:h-5 text-secondary" />}
            </button>
            <button
              onClick={() => setLanguage('es')}
              className={`p-3 sm:p-4 rounded-lg border-2 transition-all flex items-center justify-between ${
                lang === 'es' 
                  ? 'border-secondary bg-secondary/5' 
                  : 'border-border hover:border-slate-300'
              }`}
              data-testid="lang-es"
            >
              <div className="flex items-center gap-2 sm:gap-3">
                <span className="text-xl sm:text-2xl">🇪🇸</span>
                <span className="font-medium text-sm sm:text-base">Español</span>
              </div>
              {lang === 'es' && <Check className="w-4 h-4 sm:w-5 sm:h-5 text-secondary" />}
            </button>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-white rounded-xl shadow-sm border border-border p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-yellow-100 rounded-full flex items-center justify-center">
              <Bell className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-semibold">
                {lang === 'en' ? 'Notifications' : 'Notificaciones'}
              </h2>
              <p className="text-muted-foreground text-xs sm:text-sm">
                {lang === 'en' ? 'Manage email notifications' : 'Gestiona las notificaciones por email'}
              </p>
            </div>
          </div>

          <div className="space-y-3 sm:space-y-4">
            <label className="flex items-start sm:items-center justify-between py-2 sm:py-3 cursor-pointer gap-3">
              <div>
                <div className="font-medium text-sm sm:text-base">
                  {lang === 'en' ? 'New deal submissions' : 'Nuevos tratos recibidos'}
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground">
                  {lang === 'en' ? 'Get notified when someone submits a deal' : 'Recibe notificaciones de nuevos tratos'}
                </div>
              </div>
              <input
                type="checkbox"
                checked={notifications.email_deals}
                onChange={(e) => setNotifications({...notifications, email_deals: e.target.checked})}
                className="w-5 h-5 rounded border-border text-secondary focus:ring-secondary flex-shrink-0"
              />
            </label>
            <label className="flex items-start sm:items-center justify-between py-2 sm:py-3 cursor-pointer border-t border-border gap-3">
              <div>
                <div className="font-medium text-sm sm:text-base">
                  {lang === 'en' ? 'Weekly digest' : 'Resumen semanal'}
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground">
                  {lang === 'en' ? 'Weekly summary of your activity' : 'Resumen semanal de tu actividad'}
                </div>
              </div>
              <input
                type="checkbox"
                checked={notifications.email_weekly}
                onChange={(e) => setNotifications({...notifications, email_weekly: e.target.checked})}
                className="w-5 h-5 rounded border-border text-secondary focus:ring-secondary flex-shrink-0"
              />
            </label>
            <label className="flex items-start sm:items-center justify-between py-2 sm:py-3 cursor-pointer border-t border-border gap-3">
              <div>
                <div className="font-medium text-sm sm:text-base">
                  {lang === 'en' ? 'Marketing emails' : 'Emails de marketing'}
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground">
                  {lang === 'en' ? 'Tips, updates, and product news' : 'Consejos, actualizaciones y novedades'}
                </div>
              </div>
              <input
                type="checkbox"
                checked={notifications.email_marketing}
                onChange={(e) => setNotifications({...notifications, email_marketing: e.target.checked})}
                className="w-5 h-5 rounded border-border text-secondary focus:ring-secondary flex-shrink-0"
              />
            </label>
          </div>

          <button
            onClick={saveNotificationSettings}
            disabled={saving}
            className="mt-4 sm:mt-6 px-4 sm:px-6 py-2 bg-secondary text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-all text-sm"
            data-testid="save-notifications"
          >
            {saving ? '...' : (lang === 'en' ? 'Save Changes' : 'Guardar Cambios')}
          </button>
        </div>

        {/* Privacy & Data — the reader's rights over their information (Privacy Policy
            §17, §20, §23) are exercised by writing through the contact form, the channel
            the Policy names; they are handled by hand. There used to be three dedicated
            buttons here (download / privacy request / delete) with pre-written messages;
            they promised a process that did not exist behind them and were removed.
            Deleting the account and cancelling the subscription are two different things
            and are explained side by side, because that is exactly where readers confuse
            them; Billing's cancel dialog links to this card for that reason. */}
        {user && (
          <div id="privacy" className="bg-white rounded-xl shadow-sm border border-border p-4 sm:p-6 mb-4 sm:mb-6" data-testid="privacy-data-section">
            <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                <Lock className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-semibold">
                  {lang === 'en' ? 'Privacy & Data' : 'Privacidad y Datos'}
                </h2>
                <p className="text-muted-foreground text-xs sm:text-sm">
                  {lang === 'en'
                    ? 'Your rights over your information. Requests go to support; we verify your identity before acting.'
                    : 'Tus derechos sobre tu información. Las solicitudes van a soporte; verificamos tu identidad antes de actuar.'}
                </p>
              </div>
            </div>

            <Link
              to="/contact"
              className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-indigo-300 hover:bg-indigo-50/40 transition-colors"
              data-testid="privacy-contact"
            >
              <Mail className="w-5 h-5 text-indigo-600 flex-shrink-0" />
              <div className="min-w-0">
                <div className="font-medium text-sm">
                  {lang === 'en' ? 'Make a privacy request' : 'Hacer una solicitud de privacidad'}
                </div>
                <div className="text-xs text-muted-foreground">
                  {lang === 'en'
                    ? 'Access, correct, or delete your information: write to us through the contact form. We verify your identity before acting.'
                    : 'Acceder, corregir o eliminar tu información: escríbenos por el formulario de contacto. Verificamos tu identidad antes de actuar.'}
                </div>
              </div>
            </Link>

            <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-3 sm:p-4 text-xs sm:text-sm text-slate-700 space-y-2" data-testid="cancel-vs-delete">
              <div className="font-semibold text-slate-800">
                {lang === 'en' ? 'Cancelling a subscription is not deleting your account' : 'Cancelar la suscripción no es eliminar tu cuenta'}
              </div>
              <div className="flex gap-2">
                <CreditCard className="w-4 h-4 mt-0.5 text-slate-500 flex-shrink-0" />
                <div>
                  <span className="font-medium">{lang === 'en' ? 'Cancel subscription' : 'Cancelar suscripción'}</span>
                  {' — '}
                  {lang === 'en'
                    ? 'stops the charges. When the paid period ends you move to the Free plan; your account, your history and your analyses stay stored. Done from '
                    : 'detiene los cobros. Al terminar el período pagado pasas al plan Free; tu cuenta, tu historial y tus análisis siguen guardados. Se hace desde '}
                  <Link to="/billing" className="text-indigo-600 hover:text-indigo-700 underline underline-offset-2">
                    {lang === 'en' ? 'Billing' : 'Facturación'}
                  </Link>
                  .
                </div>
              </div>
              <div className="flex gap-2">
                <UserX className="w-4 h-4 mt-0.5 text-red-600 flex-shrink-0" />
                <div>
                  <span className="font-medium">{lang === 'en' ? 'Delete account' : 'Eliminar cuenta'}</span>
                  {' — '}
                  {lang === 'en'
                    ? 'permanently removes your account and personal information (any active subscription is cancelled as part of it). Requested through the contact form; see the '
                    : 'elimina de forma permanente tu cuenta y tu información personal (cualquier suscripción activa se cancela como parte del proceso). Se solicita por el formulario de contacto; ver la '}
                  <Link to="/privacy" className="text-indigo-600 hover:text-indigo-700 underline underline-offset-2">
                    {lang === 'en' ? 'Privacy Policy, section 17' : 'Política de Privacidad, sección 17'}
                  </Link>
                  .
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Danger Zone */}
        {user && (
          <div className="bg-white rounded-xl shadow-sm border border-red-200 p-4 sm:p-6">
            <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-red-600">
                  {lang === 'en' ? 'Danger Zone' : 'Zona de Peligro'}
                </h2>
                <p className="text-muted-foreground text-xs sm:text-sm">
                  {lang === 'en' ? 'Account actions' : 'Acciones de cuenta'}
                </p>
              </div>
            </div>

            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 border border-red-300 text-red-600 rounded-lg font-medium hover:bg-red-50 transition-all text-sm"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
              {lang === 'en' ? 'Sign Out' : 'Cerrar Sesión'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
